// frontend/src/pages/AdminSettings.jsx
// Phase 40 — Business Configuration Center
// All admin-configurable settings surfaced in one place.

import { useState, useEffect, useCallback } from 'react';
import api, { configAPI, feeAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import {
  Card, Button, Badge, Modal, Input,
  Spinner, Empty, Table, Td, SectionHeader,
} from '../components/ui';
import toast from 'react-hot-toast';
import { clsx } from 'clsx';
import { RefreshCw, Save, Plus, Edit2, Trash2, Check, X, AlertTriangle } from 'lucide-react';

// ── small inline API helpers not yet in api.js ────────────────
const flagsAPI = {
  getAll:  ()           => api.get('/flags'),
  toggle:  (key)        => api.put(`/flags/${key}/toggle`),
  create:  (data)       => api.post('/flags', data),
  update:  (key, d)     => api.put(`/flags/${key}`, d),
  remove:  (key)        => api.delete(`/flags/${key}`),
};

const cmsAPI = {
  getCompany:    ()    => api.get('/cms/admin/company'),
  updateCompany: (d)   => api.put('/cms/admin/company', d),
  publish:       ()    => api.post('/cms/admin/company/publish'),
};

// ── Generic category config editor ────────────────────────────
function ConfigSection({ category, onSeedRequest }) {
  const [configs, setConfigs]  = useState([]);
  const [loading, setLoading]  = useState(true);
  const [drafts, setDrafts]    = useState({});
  const [saving, setSaving]    = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await configAPI.getAll(category);
      const items = res.configs || [];
      setConfigs(items);
      const d = {};
      items.forEach(c => { d[c.key] = c.value; });
      setDrafts(d);
    } catch { toast.error('Failed to load settings'); }
    finally { setLoading(false); }
  }, [category]);

  useEffect(() => { load(); }, [load]);

  const save = async (cfg) => {
    setSaving(s => ({ ...s, [cfg.key]: true }));
    try {
      let val = drafts[cfg.key];
      if (cfg.type === 'number') val = Number(val);
      if (cfg.type === 'boolean') val = !!val;
      if (cfg.type === 'json' || cfg.type === 'array') {
        if (typeof val === 'string') val = JSON.parse(val);
      }
      await configAPI.set(cfg.key, val);
      setConfigs(prev => prev.map(c => c.key === cfg.key ? { ...c, value: val } : c));
      toast.success('Saved');
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally { setSaving(s => ({ ...s, [cfg.key]: false })); }
  };

  if (loading) return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;

  if (!configs.length) return (
    <div className="text-center py-12 space-y-3">
      <p className="text-text-muted text-sm">No settings configured for this category.</p>
      {onSeedRequest && (
        <Button size="sm" onClick={onSeedRequest}>Initialize Defaults</Button>
      )}
    </div>
  );

  return (
    <div className="space-y-2">
      {configs.map(cfg => {
        const isDirty = JSON.stringify(drafts[cfg.key]) !== JSON.stringify(cfg.value);
        return (
          <div key={cfg.key} className={clsx(
            'flex gap-4 p-3 rounded-xl border transition-all',
            cfg.multiline ? 'items-start' : 'items-center',
            cfg.isReadOnly ? 'bg-bg-panel/50 border-border opacity-60' : 'bg-bg-panel border-border'
          )}>
            <div className="flex-1 min-w-0">
              <p className="text-text-primary text-sm font-semibold">{cfg.label || cfg.key}</p>
              {cfg.description && <p className="text-text-muted text-xs mt-0.5">{cfg.description}</p>}
              <p className="font-mono text-[9px] text-text-muted mt-0.5 opacity-50">{cfg.key}</p>
            </div>
            <div className={clsx('flex gap-2 flex-shrink-0', cfg.multiline ? 'items-start' : 'items-center')}>
              {cfg.type === 'boolean' ? (
                <button
                  disabled={cfg.isReadOnly}
                  onClick={() => setDrafts(d => ({ ...d, [cfg.key]: !d[cfg.key] }))}
                  className={clsx(
                    'relative inline-flex h-6 w-11 rounded-full transition-colors flex-shrink-0',
                    drafts[cfg.key] ? 'bg-cyan' : 'bg-border',
                    cfg.isReadOnly && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <span className={clsx(
                    'inline-block h-4 w-4 m-1 rounded-full bg-white shadow transition-transform',
                    drafts[cfg.key] ? 'translate-x-5' : 'translate-x-0'
                  )} />
                </button>
              ) : (cfg.type === 'json' || cfg.type === 'array') ? (
                <input
                  type="text"
                  disabled={cfg.isReadOnly}
                  value={typeof drafts[cfg.key] === 'string' ? drafts[cfg.key] : JSON.stringify(drafts[cfg.key] ?? '')}
                  onChange={e => setDrafts(d => ({ ...d, [cfg.key]: e.target.value }))}
                  className="bg-bg border border-border rounded-xl px-3 py-2 text-text-primary text-xs outline-none focus:border-cyan w-52 font-mono"
                />
              ) : cfg.multiline ? (
                <textarea
                  disabled={cfg.isReadOnly}
                  value={drafts[cfg.key] ?? ''}
                  onChange={e => setDrafts(d => ({ ...d, [cfg.key]: e.target.value }))}
                  rows={8}
                  className="bg-bg border border-border rounded-xl px-3 py-2 text-text-primary text-sm outline-none focus:border-cyan w-80 disabled:opacity-50 resize-y"
                />
              ) : (
                <input
                  type={cfg.type === 'number' ? 'number' : 'text'}
                  disabled={cfg.isReadOnly}
                  value={drafts[cfg.key] ?? ''}
                  onChange={e => setDrafts(d => ({ ...d, [cfg.key]: e.target.value }))}
                  className="bg-bg border border-border rounded-xl px-3 py-2 text-text-primary text-sm outline-none focus:border-cyan w-44 disabled:opacity-50"
                />
              )}
              {cfg.isReadOnly
                ? <Badge label="read-only" color="gray" />
                : (
                  <button
                    disabled={saving[cfg.key] || !isDirty}
                    onClick={() => save(cfg)}
                    className={clsx(
                      'p-1.5 rounded-lg transition-all',
                      isDirty ? 'text-cyan bg-cyan/10 hover:bg-cyan/20' : 'text-text-muted opacity-30 cursor-not-allowed'
                    )}
                    title="Save"
                  >
                    {saving[cfg.key] ? <Spinner size="sm" /> : <Save className="w-4 h-4" />}
                  </button>
                )
              }
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Fee Rule Editor ────────────────────────────────────────────
const TX_TYPES = ['deposit','withdrawal','transfer','fx_conversion','merchant_payment','investment_entry','investment_exit','agent_cashin','agent_cashout','remittance'];
const FEE_TYPES = ['fixed','percentage','tiered'];
const BLANK_FEE = { name:'', code:'', transactionType:'withdrawal', feeType:'percentage', fixedAmount:0, rate:0.01, minFee:0, maxFee:0, currency:'UGX', isActive:true };

function FeesSection() {
  const [rules, setRules]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null); // null | {mode:'create'|'edit', data:{...}}
  const [form, setForm]       = useState(BLANK_FEE);
  const [saving, setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await feeAPI.getAll();
      setRules(res.rules || []);
    } catch { toast.error('Failed to load fee rules'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(BLANK_FEE); setModal({ mode: 'create' }); };
  const openEdit = (rule) => { setForm({ ...rule }); setModal({ mode: 'edit', id: rule._id }); };

  const saveRule = async () => {
    if (!form.name || !form.code || !form.transactionType) { toast.error('Name, code and type are required'); return; }
    setSaving(true);
    try {
      if (modal.mode === 'create') {
        await feeAPI.create(form);
        toast.success('Fee rule created');
      } else {
        await feeAPI.update(modal.id, form);
        toast.success('Fee rule updated');
      }
      setModal(null);
      load();
    } catch (err) { toast.error(err.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const deleteRule = async (id) => {
    setDeleting(id);
    try {
      await feeAPI.remove(id);
      toast.success('Fee rule deleted');
      setRules(r => r.filter(x => x._id !== id));
    } catch (err) { toast.error(err.message || 'Delete failed'); }
    finally { setDeleting(null); }
  };

  const txGroups = TX_TYPES.reduce((acc, t) => {
    acc[t] = rules.filter(r => r.transactionType === t);
    return acc;
  }, {});

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-text-muted text-sm">Configure fees applied to each transaction type. Rules are matched by priority (highest first).</p>
        <Button size="sm" onClick={openCreate}><Plus className="w-4 h-4" /> Add Rule</Button>
      </div>

      {loading ? <div className="flex justify-center py-12"><Spinner size="lg" /></div> : (
        <div className="space-y-4">
          {TX_TYPES.map(type => (
            <div key={type}>
              <p className="text-text-muted text-[10px] font-bold uppercase tracking-widest mb-2">
                {type.replace(/_/g, ' ')}
              </p>
              {txGroups[type].length === 0 ? (
                <div className="bg-bg-panel border border-dashed border-border rounded-xl p-3 text-center">
                  <p className="text-text-muted text-xs">No fee rules — <button onClick={openCreate} className="text-cyan hover:underline">Add one</button></p>
                </div>
              ) : (
                <Table headers={['Name', 'Type', 'Rate / Amount', 'Min', 'Max', 'Currency', 'Active', '']}>
                  {txGroups[type].map(r => (
                    <tr key={r._id} className="border-b border-border">
                      <Td>
                        <p className="font-semibold text-sm text-text-primary">{r.name}</p>
                        <p className="text-[9px] font-mono text-text-muted">{r.code}</p>
                      </Td>
                      <Td><Badge label={r.feeType} color="blue" /></Td>
                      <Td className="font-semibold text-sm">
                        {r.feeType === 'percentage' ? `${(r.rate * 100).toFixed(2)}%` : `${r.fixedAmount} ${r.currency}`}
                      </Td>
                      <Td className="text-xs text-text-muted">{r.minFee || '—'}</Td>
                      <Td className="text-xs text-text-muted">{r.maxFee || '—'}</Td>
                      <Td className="text-xs">{r.currency || 'USD'}</Td>
                      <Td><Badge label={r.isActive ? 'Active' : 'Off'} color={r.isActive ? 'green' : 'gray'} /></Td>
                      <Td>
                        <div className="flex gap-1">
                          <button onClick={() => openEdit(r)} className="p-1 text-text-muted hover:text-cyan rounded"><Edit2 className="w-3.5 h-3.5" /></button>
                          <button
                            onClick={() => deleteRule(r._id)}
                            disabled={deleting === r._id}
                            className="p-1 text-text-muted hover:text-red rounded"
                          >
                            {deleting === r._id ? <Spinner size="sm" /> : <Trash2 className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </Td>
                    </tr>
                  ))}
                </Table>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal open={!!modal} onClose={() => setModal(null)} title={modal?.mode === 'create' ? 'New Fee Rule' : 'Edit Fee Rule'} size="md">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input label="Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. UGX Withdrawal Fee" />
            <Input label="Code" value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase().replace(/\s/g,'_') }))} placeholder="e.g. UGX_WITHDRAWAL_1PCT" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-text-muted text-xs font-semibold mb-1.5 uppercase tracking-wider">Transaction Type</p>
              <select value={form.transactionType} onChange={e => setForm(f => ({ ...f, transactionType: e.target.value }))}
                className="w-full bg-bg-panel border border-border rounded-xl px-3 py-2.5 text-text-primary text-sm outline-none focus:border-cyan"
              >
                {TX_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g,' ')}</option>)}
              </select>
            </div>
            <div>
              <p className="text-text-muted text-xs font-semibold mb-1.5 uppercase tracking-wider">Fee Type</p>
              <select value={form.feeType} onChange={e => setForm(f => ({ ...f, feeType: e.target.value }))}
                className="w-full bg-bg-panel border border-border rounded-xl px-3 py-2.5 text-text-primary text-sm outline-none focus:border-cyan"
              >
                {FEE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {form.feeType === 'percentage'
              ? <Input label="Rate (e.g. 0.01 = 1%)" type="number" value={form.rate} onChange={e => setForm(f => ({ ...f, rate: parseFloat(e.target.value) || 0 }))} />
              : <Input label="Fixed Amount" type="number" value={form.fixedAmount} onChange={e => setForm(f => ({ ...f, fixedAmount: parseFloat(e.target.value) || 0 }))} />
            }
            <div>
              <p className="text-text-muted text-xs font-semibold mb-1.5 uppercase tracking-wider">Currency</p>
              <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
                className="w-full bg-bg-panel border border-border rounded-xl px-3 py-2.5 text-text-primary text-sm outline-none focus:border-cyan"
              >
                {['UGX','USD','EUR','USDT'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Min Fee (0 = none)" type="number" value={form.minFee || 0} onChange={e => setForm(f => ({ ...f, minFee: parseFloat(e.target.value) || 0 }))} />
            <Input label="Max Fee (0 = none)" type="number" value={form.maxFee || 0} onChange={e => setForm(f => ({ ...f, maxFee: parseFloat(e.target.value) || 0 }))} />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setForm(f => ({ ...f, isActive: !f.isActive }))}
              className={clsx('relative inline-flex h-6 w-11 rounded-full transition-colors', form.isActive ? 'bg-cyan' : 'bg-border')}
            >
              <span className={clsx('inline-block h-4 w-4 m-1 rounded-full bg-white shadow transition-transform', form.isActive ? 'translate-x-5' : 'translate-x-0')} />
            </button>
            <span className="text-text-primary text-sm font-semibold">Active</span>
          </div>
          <Button className="w-full" loading={saving} onClick={saveRule}>
            {modal?.mode === 'create' ? 'Create Fee Rule' : 'Save Changes'}
          </Button>
        </div>
      </Modal>
    </>
  );
}

// ── Feature Flags Manager ─────────────────────────────────────
function FeaturesSection() {
  const [flags, setFlags]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await flagsAPI.getAll();
      setFlags(res.flags || []);
    } catch { toast.error('Failed to load feature flags'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (flag) => {
    setToggling(t => ({ ...t, [flag.key]: true }));
    try {
      const res = await flagsAPI.toggle(flag.key);
      setFlags(prev => prev.map(f => f.key === flag.key ? { ...f, isEnabled: res.flag?.isEnabled ?? !f.isEnabled } : f));
      toast.success(`${flag.label || flag.key} ${res.flag?.isEnabled ? 'enabled' : 'disabled'}`);
    } catch (err) { toast.error(err.message || 'Toggle failed'); }
    finally { setToggling(t => ({ ...t, [flag.key]: false })); }
  };

  if (loading) return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;
  if (!flags.length) return <Empty icon="🚩" title="No feature flags" description="Feature flags control module visibility and availability." />;

  const groups = flags.reduce((acc, f) => {
    const g = f.category || 'general';
    if (!acc[g]) acc[g] = [];
    acc[g].push(f);
    return acc;
  }, {});

  return (
    <div className="space-y-5">
      {Object.entries(groups).map(([group, items]) => (
        <div key={group}>
          <p className="text-text-muted text-[10px] font-bold uppercase tracking-widest mb-2">{group}</p>
          <div className="space-y-2">
            {items.map(f => (
              <div key={f.key} className="flex items-center justify-between p-3 bg-bg-panel border border-border rounded-xl">
                <div>
                  <p className="text-text-primary text-sm font-semibold">{f.label || f.key}</p>
                  {f.description && <p className="text-text-muted text-xs">{f.description}</p>}
                  <p className="font-mono text-[9px] text-text-muted mt-0.5">{f.key}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    label={f.visibility || 'public'}
                    color={f.visibility === 'public' ? 'green' : f.visibility === 'coming_soon' ? 'amber' : 'gray'}
                  />
                  {toggling[f.key] ? <Spinner size="sm" /> : (
                    <button
                      onClick={() => toggle(f)}
                      className={clsx(
                        'relative inline-flex h-6 w-11 rounded-full transition-colors',
                        f.isEnabled ? 'bg-cyan' : 'bg-border'
                      )}
                    >
                      <span className={clsx(
                        'inline-block h-4 w-4 m-1 rounded-full bg-white shadow transition-transform',
                        f.isEnabled ? 'translate-x-5' : 'translate-x-0'
                      )} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Company Profile (Business Settings) ───────────────────────
function BusinessSection({ onSeedRequest }) {
  const [profile, setProfile]  = useState(null);
  const [loading, setLoading]  = useState(true);
  const [form, setForm]        = useState({});
  const [saving, setSaving]    = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await cmsAPI.getCompany();
      const p = res.profile || res.company || res.data || res;
      setProfile(p);
      setForm({
        name:     p.name || '',
        tagline:  p.tagline || '',
        website:  p.website || '',
        md:       p.md || '',
        mdPhone:  p.mdPhone || '',
        mdEmail:  p.mdEmail || '',
        regNumber:p.regNumber || '',
        tin:      p.tin || '',
        copyright:p.copyright || '',
      });
    } catch { toast.error('Failed to load company profile'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await cmsAPI.updateCompany(form);
      toast.success('Company profile saved');
      load();
    } catch (err) { toast.error(err.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-5">
      <Card>
        <SectionHeader title="Company Identity" />
        <div className="grid sm:grid-cols-2 gap-4">
          <Input label="Company Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          <Input label="Tagline" value={form.tagline} onChange={e => setForm(f => ({ ...f, tagline: e.target.value }))} />
          <Input label="Website" value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} />
          <Input label="Registration Number" value={form.regNumber} onChange={e => setForm(f => ({ ...f, regNumber: e.target.value }))} />
          <Input label="TIN" value={form.tin} onChange={e => setForm(f => ({ ...f, tin: e.target.value }))} />
          <Input label="Copyright Notice" value={form.copyright} onChange={e => setForm(f => ({ ...f, copyright: e.target.value }))} />
        </div>
      </Card>

      <Card>
        <SectionHeader title="Management / MD Contact" />
        <div className="grid sm:grid-cols-3 gap-4">
          <Input label="MD / CEO Name" value={form.md} onChange={e => setForm(f => ({ ...f, md: e.target.value }))} />
          <Input label="MD Phone" value={form.mdPhone} onChange={e => setForm(f => ({ ...f, mdPhone: e.target.value }))} />
          <Input label="MD Email" value={form.mdEmail} onChange={e => setForm(f => ({ ...f, mdEmail: e.target.value }))} />
        </div>
      </Card>

      <Card>
        <SectionHeader title="Operations Config" />
        <ConfigSection category="general" onSeedRequest={onSeedRequest} />
      </Card>

      <Button className="w-full" size="lg" loading={saving} onClick={save}>
        <Save className="w-4 h-4" /> Save Company Profile
      </Button>
    </div>
  );
}

// ── Business Readiness Audit ───────────────────────────────────
const AUDIT = [
  { module: 'Wallet Core v2.0',       status: 'READY',               note: 'Currency isolation, portfolio, ledger, exchange — Phase 38–39' },
  { module: 'Deposit Pipeline',        status: 'READY',               note: 'MTN MoMo + Airtel Money via Yo! — Phase 5–6' },
  { module: 'Withdrawal Pipeline',     status: 'READY',               note: 'Yo! Bulk Payments, admin queue, KYC enforcement — Phase 16' },
  { module: 'KYC System',              status: 'NEEDS CONFIGURATION', note: 'Approval workflow ready. Document types + thresholds need admin config.' },
  { module: 'USDT / Crypto Trading',   status: 'READY',               note: 'Buy/sell exchange, crypto withdrawal, treasury — Phase 10–11, 26–27' },
  { module: 'Investment Platform',     status: 'READY',               note: 'Funds, products, interest engine, projects — Phase 4, 19' },
  { module: 'Merchant Payments',       status: 'READY',               note: 'Pay page, payment links, settlement, reports — Phase 17' },
  { module: 'Referral System',         status: 'NEEDS CONFIGURATION', note: 'Commission rates seeded. Payout automation needs review.' },
  { module: 'Payment Gateway',         status: 'READY',               note: 'Adapter pattern, Yo!/MTN/Airtel providers — Phase 12' },
  { module: 'Fee Engine',              status: 'NEEDS CONFIGURATION', note: 'FeeRule model ready. Add rules for each transaction type.' },
  { module: 'ISP / DWiFi',            status: 'READY',               note: 'ISP payment engine, DwiConnect portal, Centipede — Phase 14–15' },
  { module: 'Treasury & Operations',   status: 'READY',               note: 'Snapshot, float, reserve ratio, approval queue — Phase 28' },
  { module: 'Multi-Country Engine',    status: 'READY',               note: 'Country model, CRUD, 2-step registration — Phase 30' },
  { module: 'CMS & Content',           status: 'READY',               note: 'Company profile, legal docs, help center, banners — Phase 33' },
  { module: 'AML / Compliance',        status: 'READY',               note: 'AML rules engine, alert queue — Phase 5' },
  { module: 'Audit & Logging',         status: 'READY',               note: 'Audit log, system logs, event tracking' },
  { module: 'Email / Notifications',   status: 'NEEDS CONFIGURATION', note: 'Email service ready. SMTP credentials needed in .env' },
  { module: 'SMS Notifications',       status: 'MISSING',             note: 'No SMS provider integrated. Configure or disable.' },
  { module: 'Push Notifications',      status: 'NEEDS CONFIGURATION', note: 'In-app notifications ready. Web push (FCM) not configured.' },
  { module: 'Security (PIN / OTP)',    status: 'READY',               note: 'Transaction PIN set/verify — Phase 39' },
  { module: 'Super Admin Center',      status: 'READY',               note: 'Overview, customers, finance, compliance — Phase 36' },
  { module: 'Executive Dashboard',     status: 'READY',               note: '13 aggregation endpoints, recharts, auto-refresh — Phase 31' },
  { module: 'Operations Center',       status: 'READY',               note: 'System health, payments, security, logs, backup — Phase 34' },
  { module: 'Forgot Password / 2FA',   status: 'READY',               note: 'Password reset, email verify — Phase 35' },
  { module: 'Feature Flags',           status: 'READY',               note: 'Visibility gates, rollout control — Phase 20' },
  { module: 'Business Config Panel',   status: 'READY',               note: 'This page — Phase 40' },
];

const STATUS_COLOR = { READY: 'green', 'NEEDS CONFIGURATION': 'amber', MISSING: 'red' };
const STATUS_ICON  = { READY: Check, 'NEEDS CONFIGURATION': AlertTriangle, MISSING: X };

function AuditSection() {
  const ready = AUDIT.filter(a => a.status === 'READY').length;
  const needs = AUDIT.filter(a => a.status === 'NEEDS CONFIGURATION').length;
  const missing = AUDIT.filter(a => a.status === 'MISSING').length;

  return (
    <div className="space-y-5">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Ready', count: ready, color: 'text-green', bg: 'bg-green/10 border-green/25' },
          { label: 'Needs Config', count: needs, color: 'text-amber', bg: 'bg-amber/10 border-amber/25' },
          { label: 'Missing', count: missing, color: 'text-red', bg: 'bg-red/10 border-red/25' },
        ].map(({ label, count, color, bg }) => (
          <div key={label} className={clsx('rounded-xl border p-4 text-center', bg)}>
            <p className={clsx('text-3xl font-black', color)}>{count}</p>
            <p className="text-text-secondary text-xs font-semibold mt-1">{label}</p>
          </div>
        ))}
      </div>

      {/* Checklist */}
      <div className="space-y-2">
        {AUDIT.map(({ module, status, note }) => {
          const Icon = STATUS_ICON[status];
          const color = STATUS_COLOR[status];
          return (
            <div key={module} className={clsx(
              'flex items-start gap-3 p-3 rounded-xl border',
              status === 'READY' ? 'bg-bg-panel border-border' :
              status === 'NEEDS CONFIGURATION' ? 'bg-amber/5 border-amber/20' :
              'bg-red/5 border-red/20'
            )}>
              <div className={clsx(
                'flex-shrink-0 w-5 h-5 mt-0.5 rounded-full flex items-center justify-center',
                status === 'READY' ? 'bg-green/20' : status === 'NEEDS CONFIGURATION' ? 'bg-amber/20' : 'bg-red/20'
              )}>
                <Icon className={clsx('w-3 h-3', `text-${color}`)} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-text-primary text-sm font-semibold">{module}</p>
                  <Badge label={status} color={color} />
                </div>
                <p className="text-text-muted text-xs mt-0.5">{note}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-cyan/10 border border-cyan/30 rounded-xl p-4">
        <p className="text-cyan font-bold text-sm mb-1">Production Readiness Score</p>
        <p className="text-text-secondary text-xs">
          {ready}/{AUDIT.length} modules ready ({Math.round(ready / AUDIT.length * 100)}%).
          Complete NEEDS CONFIGURATION items before public launch.
        </p>
        <div className="w-full bg-border rounded-full h-2 mt-2">
          <div className="bg-cyan h-2 rounded-full" style={{ width: `${Math.round(ready / AUDIT.length * 100)}%` }} />
        </div>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────
const TABS = [
  ['fees',          'Fees'],
  ['wallet',        'Wallet'],
  ['business',      'Business'],
  ['kyc',           'KYC'],
  ['security',      'Security'],
  ['notifications', 'Notifications'],
  ['referral',      'Referral'],
  ['investments',   'Investments'],
  ['captive_portal', 'Hotspot Portal'],
  ['features',      'Features'],
  ['audit',         'Audit'],
];

export default function AdminSettings() {
  const { isAdmin } = useAuth();
  const [tab, setTab]       = useState('fees');
  const [seeding, setSeeding] = useState(false);

  if (!isAdmin) return (
    <div className="flex items-center justify-center h-64">
      <div className="text-center">
        <p className="text-red text-xl mb-2">Access Denied</p>
        <p className="text-text-muted text-sm">Administrator access required.</p>
      </div>
    </div>
  );

  const seedDefaults = async () => {
    setSeeding(true);
    try {
      await api.post('/config/seed');
      toast.success('Default configuration seeded — reload any open tabs');
    } catch { toast.error('Seed failed'); }
    finally { setSeeding(false); }
  };

  return (
    <div className="space-y-6 animate-slide-up">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-black text-text-primary tracking-tight">Business Configuration</h1>
          <p className="text-text-secondary text-sm mt-1">Manage all platform settings, fees, and feature controls.</p>
        </div>
        <Button variant="outline" size="sm" loading={seeding} onClick={seedDefaults}>
          <RefreshCw className="w-4 h-4" /> Initialize Defaults
        </Button>
      </div>

      {/* Tab nav */}
      <div className="flex gap-2 flex-wrap">
        {TABS.map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={clsx(
              'px-4 py-2 rounded-xl text-sm font-semibold transition-all',
              tab === id
                ? 'bg-cyan text-bg'
                : 'bg-bg-panel text-text-secondary border border-border hover:border-border-hi'
            )}
          >{label}</button>
        ))}
      </div>

      {/* Tab content */}
      <Card>
        {tab === 'fees' && (
          <>
            <SectionHeader title="Fee Rules Manager" />
            <FeesSection />
          </>
        )}

        {tab === 'wallet' && (
          <>
            <SectionHeader title="Wallet Settings" />
            <p className="text-text-muted text-sm mb-4">Configure deposit/withdrawal limits, currencies, and wallet activation rules.</p>
            <ConfigSection category="wallet" onSeedRequest={seedDefaults} />
            <div className="mt-4 border-t border-border pt-4">
              <p className="text-text-muted text-[10px] font-bold uppercase tracking-widest mb-3">Payment Limits</p>
              <ConfigSection category="payments" onSeedRequest={seedDefaults} />
            </div>
          </>
        )}

        {tab === 'business' && (
          <>
            <SectionHeader title="Business Settings" />
            <BusinessSection onSeedRequest={seedDefaults} />
          </>
        )}

        {tab === 'kyc' && (
          <>
            <SectionHeader title="KYC Settings" />
            <p className="text-text-muted text-sm mb-4">Control identity verification requirements. Changes apply immediately.</p>
            <ConfigSection category="kyc" onSeedRequest={seedDefaults} />
          </>
        )}

        {tab === 'security' && (
          <>
            <SectionHeader title="Security Settings" />
            <p className="text-text-muted text-sm mb-4">Transaction PIN thresholds, OTP, fraud controls, and session policy.</p>
            <ConfigSection category="security" onSeedRequest={seedDefaults} />
          </>
        )}

        {tab === 'notifications' && (
          <>
            <SectionHeader title="Notification Settings" />
            <p className="text-text-muted text-sm mb-4">Control email, SMS, push, and admin alert delivery.</p>
            <ConfigSection category="notifications" onSeedRequest={seedDefaults} />
          </>
        )}

        {tab === 'referral' && (
          <>
            <SectionHeader title="Referral Commission Engine" />
            <p className="text-text-muted text-sm mb-4">
              Configure per-level, per-event commission rates. Changes take effect on the next qualifying purchase.
              Rates are percentages of the gross UGX purchase amount.
            </p>
            <ConfigSection category="referral" onSeedRequest={seedDefaults} />
          </>
        )}

        {tab === 'investments' && (
          <>
            <SectionHeader title="Investment Settings" />
            <p className="text-text-muted text-sm mb-4">Minimum/maximum amounts, maturity periods, and early exit penalties.</p>
            <ConfigSection category="investment" onSeedRequest={seedDefaults} />
          </>
        )}

        {tab === 'captive_portal' && (
          <>
            <SectionHeader title="DWIFI Captive Portal Settings" />
            <p className="text-text-muted text-sm mb-4">
              Welcome message, WiFi name, brand text, and the free-preview walled-garden allowlist shown on the public
              hotspot portal. All of this is read live by the portal — no code change or deploy needed to update it.
              Package pricing/duration is managed under ISP Payments → ISP Packages instead.
            </p>
            <ConfigSection category="captive_portal" onSeedRequest={seedDefaults} />
          </>
        )}

        {tab === 'features' && (
          <>
            <SectionHeader title="Feature Flags" />
            <p className="text-text-muted text-sm mb-4">Enable or disable platform modules. Changes take effect immediately for all users.</p>
            <FeaturesSection />
          </>
        )}

        {tab === 'audit' && (
          <>
            <SectionHeader title="Business Readiness Report" />
            <p className="text-text-muted text-sm mb-4">Module status audit — generated from deployed codebase state.</p>
            <AuditSection />
          </>
        )}
      </Card>
    </div>
  );
}
